import type {
  CreateSkillBody,
  GetSkillsQuery,
  GetSkillsResult,
  Skill,
  UpdateSkillBody,
} from '@didaskill/domain/skills/types';

import { HttpRepository } from '../httpRepository';

const e = encodeURIComponent;

const SKILLS_API_URL = '/api/v1/school/skills';

export class SkillsRepository extends HttpRepository {
  /**
   * Get a list of skills.
   *
   * @param limit - Pagination limit.
   * @param offset - Pagination offset.
   * @param q - Search text matched against the skill label.
   * @param ids - Filter by skill ids.
   * @returns List of skills and total number of skills matching the filters.
   */
  async getSkills({ limit, offset, q, ids }: GetSkillsQuery = {}): Promise<GetSkillsResult> {
    const { body } = await this.http
      .get(SKILLS_API_URL)
      .query({ limit, offset, q, 'ids[]': ids || [] });

    return body;
  }

  /**
   * Create a new skill.
   *
   * @param body - Skill data.
   * @returns Created skill.
   */
  async createSkill(body: CreateSkillBody): Promise<Skill> {
    const { body: createdSkill } = await this.http.post(SKILLS_API_URL).send(body);

    return createdSkill;
  }

  /**
   * Update an existing skill.
   *
   * @param skillId - Skill id.
   * @param body - Skill data to update.
   * @returns Updated skill.
   */
  async updateSkill(skillId: string, body: UpdateSkillBody): Promise<Skill> {
    const { body: updatedSkill } = await this.http
      .patch(`${SKILLS_API_URL}/${e(skillId)}`)
      .send(body);

    return updatedSkill;
  }

  /**
   * Delete an existing skill.
   *
   * @param skillId - Skill id.
   * @returns Void.
   */
  async deleteSkill(skillId: string): Promise<void> {
    await this.http.delete(`${SKILLS_API_URL}/${e(skillId)}`);
  }
}
